import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Loader2, Video, MessageSquare, Sparkles } from "lucide-react";

const DEFAULT_PROMPTS = [
  "dancing wildly to celebrate a big win",
  "throwing confetti and cheering",
  "riding a skateboard down a rainbow",
];

export default function DonatePage() {
  const { hostId } = useParams();
  const [params] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null); // "stripe" | "paypal"

  const [amount, setAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [message, setMessage] = useState("");
  const [videoPrompt, setVideoPrompt] = useState("");
  const [character, setCharacter] = useState("");
  const [duration, setDuration] = useState(4);

  useEffect(() => {
    if (params.get("cancelled")) toast.message("Checkout cancelled — no charge was made.");
  }, [params]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/donation/config/${hostId}`);
        setCfg(data);
        setDuration((data.durations_allowed || [4])[0]);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Host not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [hostId]);

  if (loading) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (!cfg) return (
    <div className="max-w-[720px] mx-auto px-6 py-24 text-center">
      <div className="font-display text-3xl mb-3">HOST NOT FOUND.</div>
      <Link to="/hosts" className="btn-primary">Browse Hosts</Link>
    </div>
  );

  const effectiveAmount = customAmount !== "" ? Number(customAmount) : amount;
  const amtOk = Number.isFinite(effectiveAmount) && effectiveAmount >= cfg.min_amount && effectiveAmount <= 1000;
  const videoUnlocked = amtOk && effectiveAmount >= cfg.video_min_amount;
  const paymentsOff = !cfg.payments_enabled;

  const checkout = async (provider) => {
    if (!amtOk) { toast.error(`Enter an amount of at least $${cfg.min_amount}.`); return; }
    setSubmitting(provider);
    try {
      const payload = {
        host_id: cfg.host.id,
        amount: Number(effectiveAmount),
        donor_name: donorName,
        message,
        video_prompt: videoUnlocked ? videoPrompt : "",
        character: videoUnlocked ? character : "",
        duration,
        origin_url: window.location.origin,
      };
      const url = provider === "paypal" ? "/donation/paypal/checkout" : "/donation/checkout";
      const { data } = await api.post(url, payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setSubmitting(null);
    }
  };

  return (
    <div className="max-w-[860px] mx-auto px-6 lg:px-12 py-20" data-testid="donate-page">
      {/* Header */}
      <div className="flex items-center gap-5 mb-8 pb-8 border-b border-[var(--ff-border)]">
        {cfg.show_logo_url ? (
          <img src={cfg.show_logo_url} alt="" className="w-20 h-20 object-cover ring-2 ring-[var(--ff-red)] rounded-lg" data-testid="donate-logo" />
        ) : (
          <div className="w-20 h-20 flex items-center justify-center bg-[var(--ff-red)] text-white font-display text-4xl rounded-lg">{cfg.host.name?.[0]}</div>
        )}
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-1 flex items-center gap-2"><Heart size={12} /> ▮ SEND A DONATION</div>
          <h1 className="font-display text-4xl lg:text-5xl leading-none" data-testid="donate-host-name">{cfg.host.name}</h1>
          {cfg.host.title && <p className="text-[var(--ff-text-dim)] text-sm mt-2 font-mono-ui">{cfg.host.title}</p>}
        </div>
      </div>

      {paymentsOff && (
        <div className="surface-card p-4 mb-6 border-yellow-500/40 text-yellow-300 text-sm font-mono-ui" data-testid="donate-paused-banner">
          Donations are temporarily paused by the site admin.
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Left: amount + message */}
        <div className="lg:col-span-3 space-y-6">
          <div className="surface-card p-6">
            <label className="label-eyebrow block mb-3">▮ AMOUNT</label>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {cfg.tiers.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setAmount(t); setCustomAmount(""); }}
                  className={`py-4 font-display text-2xl border transition-colors ${customAmount === "" && amount === t ? "bg-[var(--ff-red)] text-white border-[var(--ff-red)]" : "border-[var(--ff-border)] text-[var(--ff-text)] hover:border-white"}`}
                  data-testid={`donate-tier-${t}`}
                >
                  ${t}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-display text-2xl text-[var(--ff-text-dim)]">$</span>
              <input
                type="number"
                min={cfg.min_amount}
                step="1"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder={`Custom (min $${cfg.min_amount})`}
                className="flex-1 px-4 py-3 text-lg"
                data-testid="donate-custom-amount"
              />
            </div>
            <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mt-2">
              Donations of ${cfg.video_min_amount}+ generate a custom AI video.
            </div>
          </div>

          <div className="surface-card p-6 space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Display Name</label>
              <input
                type="text"
                maxLength={60}
                value={donorName}
                onChange={(e) => setDonorName(e.target.value)}
                placeholder="Shown on stream (blank = Anonymous / your Discord name)"
                className="w-full px-4 py-3 text-sm"
                data-testid="donate-name"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2 flex items-center gap-2"><MessageSquare size={12} /> Message</label>
              <textarea
                maxLength={200}
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Say something to the host…"
                className="w-full px-4 py-3 text-sm font-mono-ui leading-relaxed"
                data-testid="donate-message"
              />
              <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] text-right mt-1">{message.length}/200</div>
            </div>
          </div>
        </div>

        {/* Right: AI video + pay */}
        <div className="lg:col-span-2 space-y-6">
          <div className={`surface-card p-6 transition-opacity ${videoUnlocked ? "" : "opacity-50"}`} data-testid="donate-video-panel">
            <label className="label-eyebrow block mb-3 flex items-center gap-2"><Video size={12} /> ▮ AI VIDEO ALERT</label>
            {!videoUnlocked ? (
              <p className="text-xs text-[var(--ff-text-dim)] font-mono-ui">
                Unlock a custom AI-generated video by donating ${cfg.video_min_amount} or more.
              </p>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Video Prompt</label>
                  <textarea
                    maxLength={400}
                    rows={3}
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    placeholder={`e.g. ${DEFAULT_PROMPTS[0]}`}
                    className="w-full px-3 py-2 text-sm font-mono-ui"
                    data-testid="donate-video-prompt"
                  />
                </div>
                {cfg.characters?.length > 0 && (
                  <div>
                    <label className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Character (optional)</label>
                    <select
                      value={character}
                      onChange={(e) => setCharacter(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-[var(--ff-bg)] border border-[var(--ff-border)]"
                      data-testid="donate-character"
                    >
                      <option value="">— None —</option>
                      {cfg.characters.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-[0.65rem] font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] block mb-1">Length</label>
                  <div className="flex gap-2">
                    {(cfg.durations_allowed || [4]).map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDuration(d)}
                        className={`flex-1 py-2 text-sm border ${duration === d ? "bg-[var(--ff-red)] text-white border-[var(--ff-red)]" : "border-[var(--ff-border)] hover:border-white"}`}
                        data-testid={`donate-duration-${d}`}
                      >
                        {d}s
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="surface-card p-6">
            <div className="font-display text-3xl mb-1" data-testid="donate-total">${amtOk ? Number(effectiveAmount).toFixed(2) : "—"}</div>
            <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mb-4">
              {videoUnlocked ? "Includes AI video alert" : "Text-only alert"}
            </div>
            <div className="space-y-2">
              {cfg.stripe_enabled && (
                <button
                  type="button"
                  onClick={() => checkout("stripe")}
                  disabled={!amtOk || paymentsOff || submitting}
                  className="btn-primary w-full justify-center"
                  data-testid="donate-pay-stripe"
                >
                  {submitting === "stripe" ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <><Sparkles size={14} /> Pay with Card</>}
                </button>
              )}
              {cfg.paypal_enabled && (
                <button
                  type="button"
                  onClick={() => checkout("paypal")}
                  disabled={!amtOk || paymentsOff || submitting}
                  className="btn-ghost w-full justify-center"
                  data-testid="donate-pay-paypal"
                >
                  {submitting === "paypal" ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : "Pay with PayPal"}
                </button>
              )}
              {!cfg.stripe_enabled && !cfg.paypal_enabled && (
                <div className="text-xs text-[var(--ff-text-dim)] font-mono-ui text-center py-2">No payment method is configured.</div>
              )}
            </div>
            <p className="font-mono-ui text-[0.58rem] text-[var(--ff-text-dim)] leading-relaxed mt-4">
              Your alert is reviewed by the host before it airs on stream. Video alerts take a moment to generate.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
